// Pets: eggs come from charms and star milestones, hatch after learning rounds, and grow when
// fed with gems. One active pet follows her around. Battles come later (Pet Arena).

import type { Category } from './types';
import type { Egg, Pet, PetType, Save } from './state';
import { drawTinted, drawArt } from '@kit/assets';

export const PET_TYPES: Record<PetType, { name: string; color: string; color2: string; blurb: string }> = {
  stone: { name: 'Stone', color: '#8a5a3b', color2: '#c9a87c', blurb: 'Steady and strong. Loves rocks.' },
  leaf: { name: 'Leaf', color: '#5fbf6a', color2: '#2e7d4f', blurb: 'Quick and curious. Loves words.' },
  spark: { name: 'Spark', color: '#f5c542', color2: '#f08a3c', blurb: 'Zippy and bright. Loves numbers.' },
  wave: { name: 'Wave', color: '#3bb3a9', color2: '#4f8fe6', blurb: 'Calm and clever. Loves the sea.' },
  frost: { name: 'Frost', color: '#bfe8ff', color2: '#9b6be6', blurb: 'Cool and careful. Loves snow.' },
  star: { name: 'Star', color: '#ee86b7', color2: '#f5c542', blurb: 'Playful and lucky. Loves games.' },
};

export const HATCH_ROUNDS = 5;
export const STAGE2_FED = 60;
export const STAGE3_FED = 200;
export const FEED_COST = 10;
export const STAR_MILESTONES = [10, 25, 50, 100, 160, 240];

export function typeForCategory(c: Category): PetType {
  return c === 'words' ? 'leaf' : c === 'math' ? 'spark' : c === 'science' ? 'stone' : 'star';
}

let counter = 0;
function newId(prefix: string): string { return `${prefix}.${Date.now().toString(36)}.${(counter++).toString(36)}`; }

/** an egg of this type drops when she earns her first charm in a category she has no pet of */
export function eggForCharm(s: Save, category: Category): Egg | null {
  const type = typeForCategory(category);
  const has = s.pets.pets.some(p => p.type === type) || s.pets.eggs.some(e => e.type === type);
  if (has) return null;
  return { id: newId('egg'), type, earnedAt: Date.now(), roundsNeeded: HATCH_ROUNDS, roundsDone: 0, from: 'charm' };
}

/** star milestones drop eggs of the types she does not have yet, then wave and frost */
export function eggForStars(s: Save, stars: number): Egg | null {
  const hit = STAR_MILESTONES.find(m => stars >= m && !s.pets.milestones.includes(m));
  if (hit === undefined) return null;
  s.pets.milestones.push(hit);
  const order: PetType[] = ['star', 'wave', 'frost', 'spark', 'leaf', 'stone'];
  const owned = new Set<PetType>([...s.pets.pets.map(p => p.type), ...s.pets.eggs.map(e => e.type)]);
  const type = order.find(t => !owned.has(t)) || order[hit % order.length];
  return { id: newId('egg'), type, earnedAt: Date.now(), roundsNeeded: HATCH_ROUNDS, roundsDone: 0, from: `${hit} stars` };
}

/** every finished learning round warms every egg */
export function warmEggs(s: Save): Egg[] {
  const ready: Egg[] = [];
  for (const e of s.pets.eggs) { e.roundsDone = Math.min(e.roundsNeeded, e.roundsDone + 1); if (e.roundsDone >= e.roundsNeeded) ready.push(e); }
  return ready;
}

export function hatch(s: Save, eggId: string, name: string): Pet | null {
  const i = s.pets.eggs.findIndex(e => e.id === eggId);
  if (i < 0 || s.pets.eggs[i].roundsDone < s.pets.eggs[i].roundsNeeded) return null;
  const egg = s.pets.eggs.splice(i, 1)[0];
  const pet: Pet = { id: newId('pet'), type: egg.type, name: name.trim().slice(0, 14) || PET_TYPES[egg.type].name, stage: 1, fed: 0, bornAt: Date.now() };
  s.pets.pets.push(pet);
  if (!s.pets.active) s.pets.active = pet.id;
  return pet;
}

export function feed(s: Save, petId: string): { pet: Pet; grew: boolean } | null {
  const pet = s.pets.pets.find(p => p.id === petId);
  if (!pet || s.gems < FEED_COST) return null;
  s.gems -= FEED_COST;
  s.totals.gemsSpent += FEED_COST;
  pet.fed += FEED_COST;
  const before = pet.stage;
  if (pet.fed >= STAGE3_FED) pet.stage = 3; else if (pet.fed >= STAGE2_FED) pet.stage = 2;
  return { pet, grew: pet.stage !== before };
}

export function activePet(s: Save): Pet | undefined {
  return s.pets.pets.find(p => p.id === s.pets.active);
}

/** A small round creature: body, ears or fins by type, eyes, stage adds size and a marking. */
export function drawPet(ctx: CanvasRenderingContext2D, pet: Pick<Pet, 'type' | 'stage'>, x: number, y: number, scale: number, frame = 0) {
  const t = PET_TYPES[pet.type];
  const r = scale * (3.2 + pet.stage * 0.9);
  const bob = frame % 2 === 1 ? -scale * 0.4 : 0;
  // painted pet (ART.md section C): one white image tinted with the type color; the bob animates it
  const size = r * 3.4;
  if (drawTinted(ctx, `pet.${pet.type}.${pet.stage}`, t.color, x - size / 2, y - size * 0.9 + bob, size, size)) return;
  ctx.save();
  ctx.translate(x, y - r + bob);
  // type features behind the body
  ctx.fillStyle = t.color2;
  if (pet.type === 'leaf') { ctx.beginPath(); ctx.ellipse(0, -r * 1.1, r * 0.35, r * 0.7, 0.5, 0, 7); ctx.fill(); }
  if (pet.type === 'stone') { ctx.beginPath(); ctx.moveTo(-r * 0.8, -r * 0.4); ctx.lineTo(-r * 0.5, -r * 1.3); ctx.lineTo(-r * 0.1, -r * 0.6); ctx.fill(); ctx.beginPath(); ctx.moveTo(r * 0.8, -r * 0.4); ctx.lineTo(r * 0.5, -r * 1.3); ctx.lineTo(r * 0.1, -r * 0.6); ctx.fill(); }
  if (pet.type === 'spark') { for (let i = -1; i <= 1; i++) { ctx.beginPath(); ctx.moveTo(i * r * 0.5, -r * 0.8); ctx.lineTo(i * r * 0.5 + r * 0.25, -r * 1.5); ctx.lineTo(i * r * 0.5 + r * 0.5, -r * 0.8); ctx.fill(); } }
  if (pet.type === 'wave') { ctx.beginPath(); ctx.moveTo(-r, 0); ctx.quadraticCurveTo(-r * 1.6, -r * 0.6, -r * 1.3, -r * 1.2); ctx.quadraticCurveTo(-r * 1.1, -r * 0.4, -r * 0.6, -r * 0.3); ctx.fill(); }
  if (pet.type === 'frost') { ctx.beginPath(); ctx.arc(-r * 0.7, -r * 0.9, r * 0.35, 0, 7); ctx.arc(r * 0.7, -r * 0.9, r * 0.35, 0, 7); ctx.fill(); }
  if (pet.type === 'star') { ctx.beginPath(); for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + i * Math.PI / 5; const rr = i % 2 ? r * 0.25 : r * 0.5; ctx.lineTo(Math.cos(a) * rr, -r * 1.1 + Math.sin(a) * rr); } ctx.closePath(); ctx.fill(); }
  // body
  ctx.fillStyle = t.color;
  ctx.beginPath(); ctx.ellipse(0, 0, r, r * 0.9, 0, 0, 7); ctx.fill();
  // belly
  ctx.fillStyle = 'rgba(255,255,255,.35)';
  ctx.beginPath(); ctx.ellipse(0, r * 0.3, r * 0.55, r * 0.4, 0, 0, 7); ctx.fill();
  // stage marking
  if (pet.stage >= 2) { ctx.fillStyle = t.color2; ctx.beginPath(); ctx.arc(-r * 0.55, -r * 0.1, r * 0.14, 0, 7); ctx.arc(r * 0.55, -r * 0.1, r * 0.14, 0, 7); ctx.fill(); }
  if (pet.stage >= 3) { ctx.fillStyle = '#f5c542'; ctx.beginPath(); ctx.arc(0, -r * 0.95, r * 0.18, 0, 7); ctx.fill(); }
  // eyes
  ctx.fillStyle = '#2a2a2e';
  ctx.beginPath(); ctx.arc(-r * 0.3, -r * 0.2, r * 0.12, 0, 7); ctx.arc(r * 0.3, -r * 0.2, r * 0.12, 0, 7); ctx.fill();
  // feet
  ctx.fillStyle = t.color2;
  ctx.beginPath(); ctx.ellipse(-r * 0.45, r * 0.85, r * 0.3, r * 0.15, 0, 0, 7); ctx.ellipse(r * 0.45, r * 0.85, r * 0.3, r * 0.15, 0, 0, 7); ctx.fill();
  ctx.restore();
}

export function drawEgg(ctx: CanvasRenderingContext2D, egg: Pick<Egg, 'type' | 'roundsDone' | 'roundsNeeded'>, x: number, y: number, r: number) {
  const t = PET_TYPES[egg.type];
  // painted egg, then the warmth crack drawn on top so it still shows progress
  if (drawArt(ctx, 'pet.egg', x - r * 1.3, y - r * 1.3, r * 2.6, r * 2.6)) {
    const frac = egg.roundsDone / egg.roundsNeeded;
    if (frac > 0) { ctx.strokeStyle = '#3b2a1e'; ctx.lineWidth = Math.max(1.5, r * 0.07); ctx.beginPath(); ctx.moveTo(x - r * 0.3, y - r * 0.15); for (let i = 1; i <= Math.ceil(frac * 5); i++) ctx.lineTo(x - r * 0.3 + i * r * 0.12, y - r * 0.15 + (i % 2 ? r * 0.12 : -r * 0.08)); ctx.stroke(); }
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.fillStyle = '#f6f3ec';
  ctx.beginPath(); ctx.ellipse(0, 0, r * 0.75, r, 0, 0, 7); ctx.fill();
  ctx.fillStyle = t.color;
  for (let i = 0; i < 5; i++) { ctx.beginPath(); ctx.arc(Math.cos(i * 1.3) * r * 0.4, Math.sin(i * 2.1) * r * 0.5, r * 0.13, 0, 7); ctx.fill(); }
  // crack grows with warmth
  const frac = egg.roundsDone / egg.roundsNeeded;
  if (frac > 0) { ctx.strokeStyle = '#2a2a2e'; ctx.lineWidth = Math.max(1.5, r * 0.08); ctx.beginPath(); ctx.moveTo(-r * 0.3, -r * 0.2); for (let i = 1; i <= Math.ceil(frac * 5); i++) ctx.lineTo(-r * 0.3 + i * r * 0.12, -r * 0.2 + (i % 2 ? r * 0.12 : -r * 0.08)); ctx.stroke(); }
  ctx.restore();
}
